import { Flex, Heading } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useInvalidateOrgConfig } from "src/hooks/useOrgConfig";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as OrgService from "src/services/orgService";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { StripeConnectionCard } from "./StripeConnectionCard";

export const Form: React.FC = () => {
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const formik = useFormikContext<FormikType>();

  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Payments
      </Heading>
      <FormikSwitchableContent<true | false>
        name="payments.enabled"
        label="Collect payments"
        description="Connect a Stripe account to collect application or enrollment fees from families."
        checked={[true, ""]}
        isDisabled={!isAvelaAdmin}
        unchecked={[false, ""]}
        showContent={(value): boolean => value === true}
      >
        <Flex direction="column" gap={4} marginTop={4}>
          <StripeConnectionCard
            status={
              formik.values.payments.enabled && formik.values.payments.accountId
                ? "connected"
                : "not_connected"
            }
            stripeAccountId={
              formik.values.payments.enabled
                ? formik.values.payments.accountId
                : undefined
            }
            stripeUrl={
              formik.values.payments.enabled && formik.values.payments.accountId
                ? `https://dashboard.stripe.com/${formik.values.payments.accountId}`
                : undefined
            }
          />
        </Flex>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useService(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.Payments>("Payments");

  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config) {
        return {
          payments: {
            enabled: config.enabled,
            accountId: config.accountId,
          },
        };
      }
      return {
        payments: {
          enabled: false,
        },
      };
    });
  }, [remoteData]);

  const { scheduleOrgConfigInvalidation } = useInvalidateOrgConfig();
  const submit = useCallback(
    async (values: FormikType) => {
      // only avela admin can update payment config
      // non avela admin can only connect/disconnect stripe account
      if (!isAvelaAdmin) {
        return;
      }
      if (values.payments.enabled) {
        await update({
          enabled: true,
          accountId: values.payments.accountId,
        });
      } else {
        await update({
          enabled: false,
          accountId: values.payments.accountId,
        });
      }
      // flush org config cache for payments
      // we wait for 5s before we invalidate org config to allow CDN cache invalidation to finish.
      // it's not 100% guarantee to work all the time, but I think it's the best we can do
      scheduleOrgConfigInvalidation("Payments", { delayInMs: 5000 });
    },
    [isAvelaAdmin, update, scheduleOrgConfigInvalidation]
  );

  return { initialValues, submit };
}

export const FormikSchema = z.object({
  payments: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
      accountId: z.string().optional(),
    }),
    z.object({
      enabled: z.literal(false),
      accountId: z.string().optional(),
    }),
  ]),
});

export type FormikType = z.infer<typeof FormikSchema>;
